import * as React from 'react';
import { Button } from '@core/design-system/tailwind/ui/button';
import { cn } from '@core/design-system/tailwind/lib/utils';
import { ButtonProps } from '@core/design-system/tailwind/ui/button';

interface GradientButtonProps extends ButtonProps {
  children: React.ReactNode;
}

export const GradientButton = React.forwardRef<
  HTMLButtonElement,
  GradientButtonProps
>(({ className, children, ...props }, ref) => {
  return (
    <>
      <Button
        ref={ref}
        size="sm"
        className={cn(
          'group relative overflow-hidden w-full md:w-fit rounded-md',
          'text-secondary font-semibold text-sm px-4 h-8',
          'transition-all duration-300 ease-out hover:scale-[1.02] hover:shadow-[0_0_15px_rgba(29,221,88,0.4)]',
          className
        )}
        style={{
          background:
            'linear-gradient(90deg, #00dc65, #38b9b7, #00dc65, #38b9b7)',
          backgroundSize: '300% 100%',
          animation: 'continuousGradientFlow 6s linear infinite',
        }}
        {...props}
      >
        {/* Gradient overlay on hover */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0 pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, #00dc65, #38b9b7, #00dc65)',
            backgroundSize: '200% 100%',
            animation: 'gradientFlow 4s linear infinite',
          }}
        />

        {/* Shimmer on hover */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-300 z-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.5) 50%, transparent 70%)',
            backgroundSize: '200% 200%',
            animation: 'shimmer 1.5s ease-in-out infinite',
          }}
        />

        {/* Content */}
        <span className="relative z-10">{children}</span>

        {/* Subtle glow */}
        <div
          className="absolute inset-0 rounded-md pointer-events-none z-0 transition-all duration-300 blur-sm opacity-50 group-hover:blur-md group-hover:opacity-75"
          style={{ background: 'linear-gradient(90deg, #00dc65, #38b9b7)' }}
        />
      </Button>

      <style jsx>{`
        @keyframes continuousGradientFlow {
          0% {
            background-position: 0% 50%;
          }
          100% {
            background-position: 100% 50%;
          }
        }
        @keyframes gradientFlow {
          0% {
            background-position: 0% 50%;
          }
          100% {
            background-position: 200% 50%;
          }
        }
        @keyframes shimmer {
          0% {
            background-position: -200% -200%;
          }
          100% {
            background-position: 200% 200%;
          }
        }
      `}</style>
    </>
  );
});

GradientButton.displayName = 'GradientButton';
